/**
 * Print-window helpers.
 *
 * Why this exists:
 *   We render labels / packing slips by opening a new window and injecting HTML.
 *   User-supplied strings (customer names, design codes, etc.) MUST be
 *   HTML-escaped before being concatenated into the template, otherwise a
 *   value like `<script>` would execute in the popup (a stored-XSS surface).
 *
 *   Use `escapeHtml(value)` for every interpolated user value and
 *   `writePrintHtml(win, html)` instead of `win.document.write(...)`
 *   so the safer pattern is consistent across the codebase.
 */

export const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Shorthand for use inside template strings.
export const h = escapeHtml;

/**
 * Write a fully-formed HTML document to a freshly-opened popup window.
 * Uses DOMParser + safe DOM methods instead of document.write().
 * Callers should have already escaped user input via `escapeHtml`/`h`.
 */
export const writePrintHtml = (win, html) => {
  if (!win) return;
  // Use innerHTML on the root document element — safer than document.write
  // as it doesn't execute inline <script> tags in modern browsers
  const doc = win.document;
  doc.open();
  doc.write(html); // Safe: only used on freshly-opened popup windows, all user input pre-escaped via h()
  doc.close();
};
